import { Network } from '@capacitor/network';
import { SystemBars, SystemBarsStyle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Keyboard, type KeyboardInfo } from '@capacitor/keyboard';

import i18n from '@/i18n';
import { ref } from 'vue';
import { useAppStore } from '@/stores/app';

const { t } = i18n.global;

const connexionLastStatus = ref<boolean>(true);

function initNetworkStatus() {
  Network.addListener('networkStatusChange', status => {
    displayNetworkStatusToaster(status.connected);
  });

  Network.getStatus().then(status => {
    // Only display if status disconnected on app launch
    if (!status.connected) {
      displayNetworkStatusToaster(status.connected);
    }
  });
}

function displayNetworkStatusToaster(isConnected: boolean) {
  const appStore = useAppStore();

  // show popup connexion only if value changed
  if (isConnected && !connexionLastStatus.value) {
    appStore.showPopup({
      text: t('error.backOnline'),
      dismissible: true,
      timeout: 3000,
    });
    connexionLastStatus.value = true;
  } else if (!isConnected && connexionLastStatus.value !== isConnected) {
    appStore.showPopup({
      text: t('error.offline'),
      icon: 'globe-off',
      dismissible: true,
    });
    connexionLastStatus.value = false;
  }
}

const setSystemBarsStyle = async () => {
  await SystemBars.setStyle({ style: SystemBarsStyle.Light });
};

// Toggle a CSS class on <html> when the virtual keyboard is open, so that the
// navbar (position:fixed; bottom:0) can be hidden — otherwise it would be
// pushed up by the keyboard on Android because the WebView reports a smaller
// viewport when the IME shows.
// The keyboard height is exposed as `--keyboard-height` too: the WebView keeps
// its size (Keyboard.resize = none), so a scrollable list behind the keyboard
// needs that much bottom padding to bring its last items into view.
function initKeyboardListeners() {
  const root = document.documentElement;
  const onKeyboardShow = ({ keyboardHeight }: KeyboardInfo) => {
    root.classList.add('keyboard-open');
    root.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
  };
  const onKeyboardHide = () => {
    root.classList.remove('keyboard-open');
    root.style.setProperty('--keyboard-height', '0px');
  };
  Keyboard.addListener('keyboardWillShow', onKeyboardShow);
  Keyboard.addListener('keyboardDidShow', onKeyboardShow);
  Keyboard.addListener('keyboardWillHide', onKeyboardHide);
  Keyboard.addListener('keyboardDidHide', onKeyboardHide);
}

async function initPushNotifications() {
  try {
    const permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      const result = await PushNotifications.requestPermissions();
      if (result.receive !== 'granted') return;
    } else if (permStatus.receive !== 'granted') {
      return;
    }

    await PushNotifications.register();
  } catch (error) {
    console.error('Failed to initialize push notifications:', error);
  }
}

export function initCapacitorPlugins() {
  initNetworkStatus();
  setSystemBarsStyle();
  initKeyboardListeners();
  initPushNotifications();
}
